import { css } from '@emotion/css';
import type { ButtonProps } from '../components/Button';
import type { ContainerProps } from '../components/Container';
import type { ImageProps } from '../components/Image';
import type { PageProps } from '../components/Page';
import type { TextProps } from '../components/Text';
import type { NodeDetachablePropsState } from '../types/craft';
import type {
  PositionAnchorsState,
  StylingProps,
  RGBA,
  VisibilityState,
  Gradient,
  Color,
} from '../types/layout';
import type { ShadowSettingsDto, ShadowType } from '../dto/craft/props/shared/ShadowSettingsDto';
import ColorHelper from 'color';
import type { StyleColors, Button } from '../types/styles';
import type { ButtonSize } from '../styles/constants';
import {
  MOBILE_MEDIA_QUERY_HEADER,
  MOBILE_CONTAINER_QUERY_HEADER,
  TABLET_MEDIA_QUERY_HEADER,
  TABLET_CONTAINER_QUERY_HEADER,
} from './constants';

export const getPaddingStyle = (padding: StylingProps['padding']) => {
  const top = padding[0] || 0;
  const right = padding[1] || 0;
  const bottom = padding[2] || 0;
  const left = padding[3] || 0;

  return { padding: `${top}px ${right}px ${bottom}px ${left}px` };
};

export const getMarginStyle = (margin: StylingProps['margin']) => {
  const top = margin[0] || 0;
  const right = margin[1] || 0;
  const bottom = margin[2] || 0;
  const left = margin[3] || 0;

  return { margin: `${top}px ${right}px ${bottom}px ${left}px` };
};

export const getBorderRadiusStyle = (borderRadius: StylingProps['borderRadius']) => {
  const top = borderRadius[0] || 0;
  const right = borderRadius[1] || 0;
  const bottom = borderRadius[2] || 0;
  const left = borderRadius[3] || 0;

  return { borderRadius: `${top}px ${right}px ${bottom}px ${left}px` };
};

export const getInnerBorderRadiusStyle = (
  borderRadius: StylingProps['borderRadius'],
  borderWidth: number,
) => {
  const topLeft = Math.max(0, (borderRadius[0] || 0) - borderWidth);
  const topRight = Math.max(0, (borderRadius[1] || 0) - borderWidth);
  const bottomRight = Math.max(0, (borderRadius[2] || 0) - borderWidth);
  const bottomLeft = Math.max(0, (borderRadius[3] || 0) - borderWidth);

  return { borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px` };
};

export const getPositionStyle = (
  position?: StylingProps['position'],
  top?: string,
  left?: string,
  right?: string,
  bottom?: string,
  positionAnchorsState?: PositionAnchorsState,
): React.CSSProperties => {
  const isAbsolute = position === 'absolute';
  const isFixed = position === 'fixed';
  const style: React.CSSProperties = {
    ...(position ? { position } : {}),
  };

  if (isAbsolute || isFixed) {
    const enableLeft = positionAnchorsState?.enableLeft ?? false;
    const enableRight = positionAnchorsState?.enableRight ?? false;
    const enableTop = positionAnchorsState?.enableTop ?? false;
    const enableBottom = positionAnchorsState?.enableBottom ?? false;

    // ---- Horizontal (left/right)
    if (enableLeft && enableRight) {
      if (left) style.left = left;
      if (right) style.right = right;
    } else if (!enableLeft && !enableRight) {
      if (left) style.left = left; // fallback to left
    } else {
      if (enableLeft && left) style.left = left;
      if (enableRight && right) style.right = right;
    }

    // ---- Vertical (top/bottom)
    if (enableTop && enableBottom) {
      if (top) style.top = top;
      if (bottom) style.bottom = bottom;
    } else if (!enableTop && !enableBottom) {
      if (top) style.top = top; // fallback to top
    } else {
      if (enableTop && top) style.top = top;
      if (enableBottom && bottom) style.bottom = bottom;
    }
  } else if (position === 'sticky') {
    style.top = top ?? '0px';
  }

  return style;
};

export const getTypographyStyle = ({
  textAlign,
  shadow,
}: {
  textAlign?: React.CSSProperties['textAlign'];
  shadow?: number;
}) => {
  return {
    textAlign,
    textShadow: `0px 0px 2px rgba(0,0,0,${(shadow || 0) / 100})`,
  };
};

const formatDimension = (
  value: string | number | undefined | null,
  fallback: string = '100%',
): string => {
  if (value === undefined || value === null || value === '') return fallback;

  if (typeof value === 'number') {
    return `${value}px`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.endsWith('%') || trimmed.endsWith('px') || trimmed === 'auto') {
      return trimmed;
    }

    // If it's a number string (e.g. "100"), assume px
    if (!isNaN(Number(trimmed))) {
      return `${trimmed}px`;
    }
  }

  // fallback if all checks fail
  return fallback;
};

export const getWidthStyle = (width?: string | number | null) => ({
  width: formatDimension(width, '100%'),
});

export const getHeightStyle = (height?: string | number | null) => ({
  height: formatDimension(height, 'auto'),
});

export const rgbaToString = (rgba: RGBA | undefined): string => {
  if (!rgba) return '';
  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
};

export const getBorderStyles = (
  borderWidth?: number,
  borderColor?: RGBA | string,
  activeGlobalStyleColors?: StyleColors,
): { [key: string]: string } => {
  const width = borderWidth !== undefined ? `${borderWidth}px` : '0px';

  let resolvedBorderColor = null;
  if (borderColor) {
    if (isColorReference(borderColor)) {
      if (!activeGlobalStyleColors) {
        // No global style colors to resolve, so use transparent
        resolvedBorderColor = 'transparent';
      } else {
        const globalColor = resolveColorReference(borderColor, activeGlobalStyleColors);
        if (!globalColor) {
          // Color reference not found or invalid, use transparent
          resolvedBorderColor = 'transparent';
        } else if (checkIsGradient(globalColor)) {
          // Border color doesn't support gradients so this is an invalid state. We handle this
          // gracefully by using the first stop color as the border color.
          resolvedBorderColor = globalColor.stops[0].hexa;
        } else {
          resolvedBorderColor = rgbaToString(globalColor);
        }
      }
    } else if (typeof borderColor === 'string') {
      resolvedBorderColor = 'transparent';
    } else {
      resolvedBorderColor = rgbaToString(borderColor);
    }
  } else {
    resolvedBorderColor = 'transparent';
  }

  return {
    borderColor: resolvedBorderColor,
    borderStyle: borderWidth ? 'solid' : 'none',
    borderWidth: width,
  };
};

export const DEFAULT_VISIBILITY: VisibilityState = {
  desktop: true,
  tablet: true,
  mobile: true,
};

export const getVisibilityClasses = (visibility: VisibilityState): string => {
  return `${!visibility.desktop ? 'desktop-hidden' : ''} ${!visibility.tablet ? 'tablet-hidden' : ''} ${!visibility.mobile ? 'mobile-hidden' : ''}`;
};

export const getVisibilityStyle = (visibilityValue: boolean | undefined): 'visible' | 'hidden' => {
  return visibilityValue ? 'visible' : 'hidden';
};

export const getOpacityStyle = (opacity?: number) => ({
  opacity: opacity !== undefined ? opacity : 1,
});

export const rgbaToHexa = (color: Color): string => {
  if (!color) return '';
  if (checkIsGradient(color)) return '';
  // After checking isGradient, TypeScript knows it's RGBA
  const { r, g, b, a } = color;
  const colorHelper = ColorHelper({ r, g, b, alpha: a });
  return colorHelper.hexa().replace('#', '');
};

/**
 * Converts a Color (RGBA or Gradient) to a hex string suitable for ColorField input.
 * For gradients, returns the first stop's color. Returns hex without '#' prefix.
 */
export const colorToHex = (color: Color | undefined): string => {
  if (!color) return '';
  if ('stops' in color) {
    // Gradient - use first stop color
    return color.stops[0]?.hexa?.replace('#', '') || '';
  }
  // RGBA
  return rgbaToHexa(color).replace('#', '');
};

/**
 * Converts a hex color string to an RGBA object.
 * Supports 3-digit shorthand (e.g., "fff"), 6-character, and 8-character hex strings.
 */
export const hexToRgba = (hex: string): RGBA | undefined => {
  if (!hex) return undefined;
  let cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (![6, 8].includes(cleaned.length)) return undefined;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  const a = cleaned.length === 8 ? parseInt(cleaned.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
};

export const getRgbaFromHex = (hex: string): RGBA | null => {
  if (!(hex || '').trim()) return null;
  let cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (![6, 8].includes(cleaned.length)) return null;
  const color = ColorHelper(`#${cleaned}`);
  return {
    r: color.red(),
    g: color.green(),
    b: color.blue(),
    a: color.alpha(),
  };
};

/**
 * Validates if a hex color string is in a valid format.
 * Supports 3-digit shorthand (e.g., "fff"), 6-character (e.g., "ffffff"),
 * and optionally 8-character with alpha (e.g., "ffffffff").
 *
 * @param hex - The hex string to validate (with or without '#' prefix)
 * @param allowAlpha - Whether to allow 8-character hex with alpha channel (default: false)
 * @returns true if the hex string is valid, false otherwise
 *
 * @example
 * isValidHex('fff') // true
 * isValidHex('#ffffff') // true
 * isValidHex('ffffffff', true) // true
 * isValidHex('gg') // false
 */
export const isValidHex = (hex: string, allowAlpha: boolean = false): boolean => {
  if (!hex) return false;
  const cleaned = hex.replace(/^#/, '').trim();

  if (allowAlpha) {
    // Allow 3, 6, or 8 character hex (case-insensitive)
    return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(cleaned);
  }

  // Only allow 3 or 6 character hex (case-insensitive)
  return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(cleaned);
};

// Helper function to get background size based on mode
export const getBackgroundSize = (mode: string, scale: number) => {
  const scalePercent = `${scale}%`;

  switch (mode) {
    case 'fill':
      return 'cover';
    case 'fit':
      return 'contain';
    case 'stretch':
      return '100% 100%';
    case 'tile':
      return scalePercent;
    default:
      return 'cover';
  }
};

// Helper function to get background repeat based on mode
export const getBackgroundRepeat = (mode: string) => {
  return mode === 'tile' ? 'repeat' : 'no-repeat';
};

// Shared utility functions for styling

/**
 * Converts a 4-element array to a CSS string with 'px' units
 * @param values - Array of 4 numbers [top, right, bottom, left]
 * @returns CSS string like "10px" or "10px 20px 10px 20px"
 */
export const toCSSSpacingString = (values: number[]) => {
  const top = values[0] || 0;
  const right = values[1] || 0;
  const bottom = values[2] || 0;
  const left = values[3] || 0;

  // Use shorthand when all values are the same
  if (top === right && right === bottom && bottom === left) {
    return `${top}px`;
  }

  // Use shorthand when top/bottom and left/right are the same
  if (top === bottom && left === right) {
    return `${top}px ${right}px`;
  }

  // Use full longhand format
  return `${top}px ${right}px ${bottom}px ${left}px`;
};

export const computeBackgroundImageStyle = (props: Partial<PageProps | ContainerProps>) => {
  const { bgImage, bgImageScale, bgImageOpacity, bgImageMode, bgImagePosition } = props;

  const backgroundImage = bgImage ? `url(${bgImage})` : undefined;
  const backgroundSize = bgImageMode
    ? getBackgroundSize(bgImageMode, bgImageScale || 100)
    : undefined;
  const backgroundRepeat = bgImageMode ? getBackgroundRepeat(bgImageMode) : undefined;
  const backgroundPosition = bgImagePosition ? `${bgImagePosition}` : undefined;
  const opacity = bgImageOpacity ? `${bgImageOpacity / 100}` : undefined;

  return {
    backgroundImage,
    backgroundSize,
    backgroundRepeat,
    backgroundPosition,
    opacity,
  };
};

export const getBgImageScaleProps = (
  props: Partial<PageProps | ContainerProps>,
  breakpointSpecificProps: Partial<PageProps | ContainerProps>,
) => {
  const imageScaleProps: Partial<PageProps | ContainerProps> = {};
  if (breakpointSpecificProps.bgImageScale || breakpointSpecificProps.bgImageMode) {
    imageScaleProps['bgImageScale'] = props.bgImageScale;
    imageScaleProps['bgImageMode'] = props.bgImageMode;
  }
  return imageScaleProps;
};

/**
 * Returns position-related props by merging detachedProps and props.
 *
 * Purpose:
 * To return all position-related properties, using values from detachedProps if present, otherwise from props.
 * This handles cases where detachedProps only contains values that differ from props.
 *
 * @param detachedProps - An object with possibly partial position-related props.
 * @param props - The main props object to fall back to.
 * @returns An object containing the merged position-related properties.
 */
// remove when done with all migrations
export function getPositionProps(
  detachedProps: NodeDetachablePropsState,
  props: Partial<TextProps | ButtonProps | ContainerProps | ImageProps>,
) {
  const positionKeys = ['position', 'positionAnchorsState', 'top', 'left', 'right', 'bottom'];
  const hasPositionProp = positionKeys.some(
    (key) => detachedProps[key as keyof NodeDetachablePropsState] !== undefined,
  );

  if (!hasPositionProp) {
    //if not a single position prop is present, return an empty object to avoid adding desktop postion props in media queries
    return {};
  }

  const positionProps = {
    ...props,
    ...detachedProps,
  };

  return {
    position: positionProps.position,
    positionAnchorsState: positionProps.positionAnchorsState,
    top: positionProps.top,
    left: positionProps.left,
    right: positionProps.right,
    bottom: positionProps.bottom,
  };
}

export function getPositionPropsV2(
  detachedProps: Partial<TextProps | ButtonProps | ContainerProps | ImageProps>,
  props: Partial<TextProps | ButtonProps | ContainerProps | ImageProps>,
) {
  const positionKeys = ['position', 'positionAnchorsState', 'top', 'left', 'right', 'bottom'];
  const hasPositionProp = positionKeys.some(
    (key) => detachedProps[key as keyof typeof detachedProps] !== undefined,
  );

  if (!hasPositionProp) {
    //if not a single position prop is present, return an empty object to avoid adding desktop postion props in media queries
    return {};
  }

  const positionProps = {
    ...props,
    ...detachedProps,
  };

  return {
    position: positionProps.position,
    positionAnchorsState: positionProps.positionAnchorsState,
    top: positionProps.top,
    left: positionProps.left,
    right: positionProps.right,
    bottom: positionProps.bottom,
  };
}

export function getGradientString(backgroundColor: Gradient | RGBA): string {
  if (!checkIsGradient(backgroundColor)) {
    return '';
  }
  if (backgroundColor.type === 'Linear') {
    return `linear-gradient(${backgroundColor.angle || 0}deg, ${backgroundColor.stops.map((stop) => `${stop.hexa} ${stop.position * 100}%`).join(', ')})`;
  } else if (backgroundColor.type === 'Radial') {
    //in future to handle center point and shape coordinates to support oncanvas controls
    return `radial-gradient(${backgroundColor.shapeCoords?.x || 50}% ${backgroundColor.shapeCoords?.y || 50}% at ${backgroundColor.centerPoint?.x || 50}% ${backgroundColor.centerPoint?.y || 50}%, ${backgroundColor.stops.map((stop) => `${stop.hexa} ${stop.position * 100}%`).join(', ')})`;
  }
  return '';
}

export function checkIsGradient(color: Color | string): color is Gradient {
  if (typeof color === 'string') {
    return false;
  }
  if (!color) {
    return false;
  }
  if (!('type' in color)) {
    return false;
  }
  return color.type === 'Linear' || color.type === 'Radial';
}

/**
 * Processes a color update value for use in state updates.
 * Handles the common pattern: undefined = no change, null = clear, Color = set (cloned).
 *
 * @param color - The color value to process (Color | null | undefined)
 * @returns undefined if no change, null if explicit clear, or cloned Color object
 *
 * @example
 * const handleColorChange = (color: Color | null | undefined) => {
 *   const processed = processColorUpdate(color);
 *   if (processed === undefined) return; // No change
 *   setColor(processed); // null or cloned Color
 * };
 */
export function processColorUpdate(color: Color | null | undefined): Color | null | undefined {
  if (color === undefined) {
    return undefined; // No change - caller should return early
  }
  if (color === null) {
    return null; // Explicit clear
  }
  return structuredClone(color); // Clone to avoid mutations
}

// Checks whether a global styles color is a color reference string
export const isColorReference = (value: Color | string): value is string => {
  const colorRefPattern = /^\$ref:#\/colors\/color[1-6]$/;
  if (typeof value === 'string' && colorRefPattern.test(value)) {
    return true;
  }
  return false;
};

// Converts a color to a CSS string
export function getColor(color: Color | undefined | null) {
  if (!color) {
    return '';
  }
  // If no type is set we can assume it is solid of type RGBA
  if (!('type' in color)) {
    return rgbaToString(color);
  }
  return getGradientString(color);
}

// Resolves the color from the color reference string and global colors if it exists
export const resolveColorReference = (ref: string, styleColors: StyleColors): Color | undefined => {
  if (!isColorReference(ref)) return undefined;

  const parts = ref.split('/');

  const colorKey = parts[2];
  if (!(colorKey in styleColors)) {
    return undefined;
  }

  return styleColors[colorKey].value;
};

// Resolves a global styles color to the CSS color string
export function getGlobalStyleColor(
  value: string | Color | null | undefined,
  styleColors: StyleColors,
): string {
  if (!value) {
    return '';
  }

  if (isColorReference(value)) {
    const ref = value as string; // type is narrowed
    const color = resolveColorReference(ref, styleColors);
    return getColor(color);
  }

  // color could still be a string but not a valid color reference
  if (typeof value === 'string') {
    return '';
  }

  return getColor(value);
}

// Resolves a global styles color to the CSS styles object
export function getGlobalTextColorStyle(
  value: Color | string | null | undefined,
  styleColors: StyleColors,
) {
  if (!value) {
    return { color: '' };
  }

  let isGradient = false;
  if (isColorReference(value)) {
    const ref = value as string; // type is narrowed
    const color = resolveColorReference(ref, styleColors);
    if (!color) return { color: '' };
    isGradient = checkIsGradient(color);
  } else if (typeof value !== 'string') {
    isGradient = checkIsGradient(value);
  }

  const colorString = getGlobalStyleColor(value, styleColors);
  if (isGradient) {
    return {
      background: colorString,
      backgroundClip: 'text',
      color: 'transparent',
    };
  } else {
    return {
      color: colorString,
    };
  }
}

export function getTextColorStyle(color: Color) {
  const isGradient = checkIsGradient(color);
  const colorString = getColor(color);
  if (isGradient) {
    return {
      background: colorString,
      backgroundClip: 'text',
      color: 'transparent',
    };
  } else {
    return {
      color: colorString,
    };
  }
}

/**
 * Adds display: 'inline-block' to text color styles when they contain a gradient.
 * This is required for background-clip: text to work properly with inline styles.
 */
export function getTextColorStyleWithDisplay(textColorStyle: {
  background?: string | number;
  [key: string]: unknown;
}): { [key: string]: unknown } {
  return textColorStyle.background
    ? { ...textColorStyle, display: 'inline-block' }
    : textColorStyle;
}

// Converts a color to CSS styling
export function getTextColorCSS(color: Color | undefined) {
  const styles = getTextColorStyle(color);
  return Object.entries(styles)
    .map(([key, value]) => {
      const cssKey = key === 'backgroundClip' ? 'background-clip' : key;
      return `${cssKey}: ${value};`;
    })
    .join(' ');
}

// Converts a global styles color to CSS styling
export function getGlobalTextColorCSS(color: Color | string, styleColors: StyleColors) {
  const styles = getGlobalTextColorStyle(color, styleColors);
  const stylesWithDisplay = getTextColorStyleWithDisplay(styles);
  return Object.entries(stylesWithDisplay)
    .map(([key, value]) => {
      const cssKey = key === 'backgroundClip' ? 'background-clip' : key;
      return `${cssKey}: ${value};`;
    })
    .join(' ');
}

/**
 * Computes Emotion CSS class from Button data for consistent styling across components.
 * This function matches the approach used in buttonVariants.ts to ensure consistency
 * between the actual Button component and preview components.
 *
 * @param button - The Button data object from global styles
 * @param size - The size variant to use for fontSize and layout styles (padding, margin, border-radius)
 * @param styleColors - StyleColors object for resolving $ref: color references
 * @param fontSizeUnit - Font size unit (defaults to 'px')
 * @param display - Display type: 'inline-flex' for preview (centers text), 'inline-block' for actual buttons (defaults to 'inline-flex')
 * @returns Emotion CSS class (string) that can be applied via className
 */
export function getButtonStyleFromData(
  button: Button,
  size: ButtonSize = 'medium',
  styleColors: StyleColors,
  fontSizeUnit: string = 'px',
  display: 'inline-flex' | 'inline-block' = 'inline-flex',
): string {
  // Get size-specific data
  const sizeData = button[size];

  const paddingStyle = getPaddingStyle(sizeData.padding);
  const marginStyle = getMarginStyle(sizeData.margin);
  const borderRadiusStyle = getBorderRadiusStyle(sizeData.borderRadius);
  const fontSize = `${sizeData.fontSize}${fontSizeUnit}`;
  const backgroundColorCss = button.backgroundColor
    ? getGlobalStyleColor(button.backgroundColor, styleColors)
    : 'transparent';
  const borderColorCss = button.borderColor
    ? getGlobalStyleColor(button.borderColor, styleColors)
    : 'transparent';
  const backgroundHoverColorCss = button.backgroundHoverColor
    ? getGlobalStyleColor(button.backgroundHoverColor, styleColors)
    : null;
  const borderHoverColorCss = button.borderHoverColor
    ? getGlobalStyleColor(button.borderHoverColor, styleColors)
    : null;

  const textColorCSS = getGlobalTextColorCSS(button.fontColor, styleColors);

  return css`
    /* Essential button styles */
    display: ${display};
    ${display === 'inline-flex' ? 'align-items: center; justify-content: center;' : ''}
    text-decoration: none;

    /* Typography */
    font-family: ${button.fontFamily};
    font-weight: ${button.fontWeight};
    letter-spacing: ${button.letterSpacing}em;
    font-size: ${fontSize};

    /* Layout */
    padding: ${paddingStyle.padding};
    margin: ${marginStyle.margin};
    border-radius: ${borderRadiusStyle.borderRadius};
    border-width: ${button.borderWidth}px;
    border-style: solid;
    border-color: ${borderColorCss};

    /* Background */
    background: ${backgroundColorCss};

    /* Interactive styles */
    pointer-events: auto;
    cursor: pointer;

    /* Text color styles on child span */
    span {
      ${textColorCSS}
    }

    /* Hover states */
    &:hover {
      ${backgroundHoverColorCss ? `background: ${backgroundHoverColorCss};` : ''}
      ${borderHoverColorCss ? `border-color: ${borderHoverColorCss};` : ''}
      ${button.fontHoverColor
        ? `span { ${getGlobalTextColorCSS(button.fontHoverColor, styleColors)} }`
        : ''}
    }
  `;
}
export function getMediaQueryHeader(isServerRendering: boolean, bp: 'mobile' | 'tablet') {
  if (bp === 'mobile') {
    return isServerRendering ? MOBILE_MEDIA_QUERY_HEADER : MOBILE_CONTAINER_QUERY_HEADER;
  } else if (bp === 'tablet') {
    return isServerRendering ? TABLET_MEDIA_QUERY_HEADER : TABLET_CONTAINER_QUERY_HEADER;
  }
  return '';
}

export function getBoxShadow(
  shadowType: ShadowType | undefined,
  shadowSettings: ShadowSettingsDto | undefined,
): string | undefined {
  if (shadowType === 'drop' && shadowSettings) {
    return `${shadowSettings.x}px ${shadowSettings.y}px ${shadowSettings.blur}px ${shadowSettings.spread}px ${shadowSettings.color}`;
  }
  return undefined;
}
